import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Tumble's design constants.
 *
 * A toy and baby shop that reads like a playroom: warm cream pages, a grape
 * violet that signs the masthead, the buttons and the footer, and one
 * sunshine-yellow note on the announcement rail and every price drop. Headings
 * are set in a rounded display face, centred, and every corner on the page is
 * soft - pill buttons, rounded product tiles - because nothing in a nursery
 * has a sharp edge.
 *
 * The shop it is previewed with is a toy and baby store stocked with the
 * platform's own Toys & Baby library pictures - see `catalog.ts` for why it
 * ships its own, and `photos.ts` for where each picture came from.
 */
export const tokens: ThemeTokens = {
  // Round-shouldered and friendly, with open counters that stay legible at a
  // child's-book size. Set at 600 so a heading is bold without being loud.
  headingFont: 'Fredoka',
  // Sunshine yellow: the rail, the sale badge and the saving line. It is not
  // the primary - yellow buttons with cream labels would not read - so it is
  // the note the theme keeps for itself, against the violet.
  accent: '#ffb627',
  monoFont: null,
  headingTracking: '0',
  headingTransform: 'none',
  // Every section is announced from the centre, the way a picture book sets a
  // title over its page.
  headingAlign: 'center',
  // Stars stay on the product page: a parent picks a toy from the picture, the
  // name and the price, and five yellow stars under every tile is noise.
  cardRatings: false,
  containerMax: '82rem',
  // A little more air than the base: a playroom is not a crowded shelf.
  spaceScale: 1.1,
  // The yellow rail and one violet deck.
  headerHeight: '7rem',
};

export const meta: ThemeMeta = {
  label: 'Tumble',
  hint: 'A playful toy and baby shop: grape violet and sunshine yellow, rounded tiles and a mega-menu masthead.',
  industries: ['toys_baby'],
  look: {
    colors: {
      // Grape violet as the primary: a solid button paints its label in the
      // page colour, so the buttons come out violet with cream labels.
      primary: '#5b3fd6',
      background: '#fffaf3',
      surface: '#f3efff',
      text: '#2a2346',
      muted: '#7a7394',
    },
    typography: {
      font_family: 'Nunito',
      heading_font: 'Fredoka',
      heading_weight: '600',
    },
    // Pill buttons: soft enough to be squeezed.
    buttons: { radius_px: 32, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`.
  },
};
